"use client";

import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import JoditEditor from "jodit-react";
import axios from "axios";
import { createMRTColumnHelper } from "material-react-table";
import { Icon } from "@iconify/react";
import Chip from "@mui/material/Chip";
import { Box, Tooltip, IconButton } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import { X, Edit, Trash2, Copy, Eye } from "lucide-react";
import { baseUrl } from "../../../../helper/urls";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import AdminCustomTable from "../../../../components/custom_table/AdminCustomTable";

const AddBlogs = () => {
  const { token } = useSelector((state) => state.auth);
  const editor = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [blogLoading, setBlogLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState({});
  const [editLoading, setEditLoading] = useState({});
  const [deleteLoading, setDeleteLoading] = useState({});

  const [categoryId, setCategoryId] = useState("");
  const [banner, setBanner] = useState(null);
  const [title, setTitle] = useState("");
  const [metaTags, setMetaTags] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");

  const [categories, setCategories] = useState([]);
  const [categoryModal, setCategoryModal] = useState(false);
  const [imageModal, setImageModal] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");

  const [uploadedImages, setUploadedImages] = useState([]);
  const [banners, setBanners] = useState([]);

  const [blogsDataList, setBlogsDataList] = useState([]);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Reset form state
  const resetFormState = () => {
    setTitle("");
    setCategoryId("");
    setMetaTags("");
    setDescription("");
    setContent("");
    setBanner(null);
    setEditingBlogId(null);
    setIsEditMode(false);
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setCategoryLoading(true);
      const res = await axios.get(`${baseUrl}/api/blog/category/list`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data?.data) {
        setCategories(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch categories", err);
      toast.error("Failed to load categories");
    } finally {
      setCategoryLoading(false);
    }
  };

  // Fetch blogs list
  const fetchBlogsList = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${baseUrl}/api/blog/getAll`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.data) {
        setBlogsDataList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching Blogs", error);
      toast.error("Failed to load blogs");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchCategories();
      fetchBanners();
      fetchBlogsList();
    }
  }, [token]);

  // Create new category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      toast.warning("Please enter a category name");
      return;
    }

    try {
      setCategoryLoading(true);
      const response = await axios.post(
        `${baseUrl}/api/blog/category/create`,
        {
          name: newCategory.trim(),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setNewCategory("");
        await fetchCategories();
        toast.success("Category created successfully!");
      }
    } catch (err) {
      console.error("Failed to create category", err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Failed to create category");
      }
    } finally {
      setCategoryLoading(false);
    }
  };

  // Delete category with confirmation
  const handleDeleteCategory = async (id, categoryName) => {
    const result = await Swal.fire({
      html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
      text: "You won't be able to revert!",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      setIsLoading(true);

      const response = await axios.delete(
        `${baseUrl}/api/blog/category/destroy/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success || response.status === 200) {
        await fetchCategories();
        toast.success("Category deleted successfully!");
      }
    } catch (err) {
      console.error("Failed to delete category", err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Failed to delete category");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Update category
  const handleUpdateCategory = async (id, newName) => {
    if (!newName.trim()) {
      toast.warning("Please enter a valid category name");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${baseUrl}/api/blog/category/update/${id}`,
        {
          name: newName.trim(),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success || response.status === 200) {
        setEditingCategory(null);
        setEditCategoryName("");
        await fetchCategories();
        toast.success("Category updated successfully!");
      }
    } catch (err) {
      console.error("Failed to update category", err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Failed to update category");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Start editing category
  const startEditingCategory = (category) => {
    setEditingCategory(category.id);
    setEditCategoryName(category.name);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingCategory(null);
    setEditCategoryName("");
  };

  // Upload Images
  const handleUploadImages = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      toast.warning("Please select at least one image");
      return;
    }

    const images = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
    }));
    setUploadedImages([...uploadedImages, ...images]);
    toast.success(`${files.length} image(s) added for upload`);
  };

  // Create/Upload banners to server
  const handleCreateBanner = async () => {
    if (!uploadedImages.length) {
      toast.warning("Please select images to upload");
      return;
    }

    try {
      setImageLoading(true);
      const uploadPromises = uploadedImages.map(async (image) => {
        const formData = new FormData();
        formData.append("banner", image.file);

        return axios.post(`${baseUrl}/api/blog/banner/create`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      });

      await Promise.all(uploadPromises);
      await fetchBanners();
      setUploadedImages([]);
      toast.success("Images uploaded successfully!");
    } catch (err) {
      console.error("Failed to upload images", err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Failed to upload images");
      }
    } finally {
      setImageLoading(false);
    }
  };

  // Fetch banners from server
  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${baseUrl}/api/blog/banner/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data?.data) {
        setBanners(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch banners", err);
      toast.error("Failed to load uploaded images");
    }
  };

  // Delete banner with confirmation
  const handleDeleteBanner = async (id, bannerName) => {
    const result = await Swal.fire({
      html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
      text: "Do you want to delete this image? This action cannot be undone.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      setIsLoading(true);

      const response = await axios.delete(
        `${baseUrl}/api/blog/banner/destroy/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success || response.status === 200) {
        await fetchBanners();
        toast.success("Image deleted successfully!");
      }
    } catch (err) {
      console.error("Failed to delete banner", err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Failed to delete image");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Copy image link
  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Image link copied to clipboard!");
  };

  // Delete local image (before upload)
  const handleDeleteImage = (index) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to remove this image from the upload queue?"
    );

    if (!confirmDelete) return;

    const updated = [...uploadedImages];
    updated.splice(index, 1);
    setUploadedImages(updated);
    toast.info("Image removed from upload queue");
  };

  // Create or Update blog post
  const handleSubmitBlog = async (e) => {
    e.preventDefault();

    // Validation
    if (!title.trim()) {
      toast.warning("Please enter a title");
      return;
    }
    if (!categoryId) {
      toast.warning("Please select a category");
      return;
    }
    if (!content || content.trim() === "") {
      toast.warning("Please enter content");
      return;
    }

    try {
      setBlogLoading(true);
      const formData = new FormData();

      // Use the exact keys as specified in the API payload
      formData.append("title", title.trim());
      formData.append("categoryId", categoryId);
      formData.append("meta_tag", metaTags.trim());
      formData.append("description", description.trim());
      formData.append("content", content);

      if (banner) {
        formData.append("banner", banner);
      }

      let response;
      if (isEditMode && editingBlogId) {
        response = await axios.post(
          `${baseUrl}/api/blog/update/${editingBlogId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(`${baseUrl}/api/blog/create`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (response.data.success || response.status === 200) {
        resetFormState();
        await fetchBlogsList();
        toast.success(
          `Blog post ${isEditMode ? "updated" : "created"} successfully!`
        );
      }
    } catch (err) {
      console.error(`Failed to ${isEditMode ? "update" : "create"} blog`, err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(`Failed to ${isEditMode ? "update" : "create"} blog post`);
      }
    } finally {
      setBlogLoading(false);
    }
  };

  // Handle edit blog button click
  const handleEditBlog = (id, row) => {
    setTitle(row.title || "");
    setCategoryId(row.categoryId?.toString() || "");
    setMetaTags(row.meta_tag || "");
    setDescription(row.description || "");
    setContent(row.content || "");
    setBanner(null);

    setEditingBlogId(id);
    setIsEditMode(true);

    toast.info("Blog loaded for editing");
  };

  // Handle reset/cancel
  const handleResetForm = () => {
    if (
      isEditMode ||
      title ||
      content ||
      categoryId ||
      metaTags ||
      description
    ) {
      Swal.fire({
        html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
        text: "Do you want to reset all form data? This action cannot be undone.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, reset it!",
      }).then((result) => {
        if (result.isConfirmed) {
          resetFormState();
          toast.info("Form has been reset");
        }
      });
    } else {
      resetFormState();
    }
  };

  // Toggle blog publish/unpublish status
  const handleToggleStatus = async (id, currentStatus) => {
    setStatusLoading((prev) => ({ ...prev, [id]: true }));
    try {
      const response = await axios.post(
        `${baseUrl}/api/blog/toggle/${id}`,
        { status: currentStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success || response.status === 200) {
        await fetchBlogsList();
        toast.success(
          `Blog ${
            currentStatus == 0 ? "published" : "unpublished"
          } successfully!`
        );
      }
    } catch (error) {
      console.error("Error toggling blog status:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update blog status");
      }
    } finally {
      setStatusLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  // Delete blog
  const handleDeleteBlog = async (id) => {
    const result = await Swal.fire({
      html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
      text: "Do you really want to delete this blog? This action cannot be undone.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        setDeleteLoading((prev) => ({ ...prev, [id]: true }));
        const response = await axios.delete(
          `${baseUrl}/api/blog/destroy/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success || response.status === 200) {
          await fetchBlogsList();
          toast.success("Blog deleted successfully!");
        }
      } catch (error) {
        console.error("Error deleting Blog", error);
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to delete blog");
        }
      } finally {
        setDeleteLoading((prev) => ({ ...prev, [id]: false }));
      }
    }
  };

  const columnHelper = createMRTColumnHelper();
  const columns = [
    // columnHelper.accessor("id", { header: "ID" }),
    columnHelper.accessor("image_url", {
      header: "Image",
      Cell: (cell) => {
        const imageUrl = cell.row.original?.image_url;
        return imageUrl ? (
          <img
            src={`${baseUrl}${imageUrl}`}
            alt="Banner"
            style={{
              width: "50px",
              height: "50px",
              objectFit: "cover",
              borderRadius: "50%",
            }}
          />
        ) : (
          "-"
        );
      },
    }),
    columnHelper.accessor("categoryName", {
      header: "Category",
      Cell: (cell) => cell.row.original?.categoryName || "-",
    }),
    columnHelper.accessor("title", {
      header: "Title",
      Cell: (cell) => cell.row.original?.title || "-",
    }),

    columnHelper.accessor("meta_tag", {
      header: "Meta Tags",
      Cell: (cell) => cell.row.original?.meta_tag || "-",
    }),
    columnHelper.accessor("description", {
      header: "Description",
      Cell: (cell) => {
        const desc = cell.row.original?.description || "-";
        return desc.length > 50 ? `${desc.substring(0, 50)}...` : desc;
      },
    }),

    columnHelper.accessor("content", {
      header: "Content",
      Cell: (cell) => {
        const content = cell.row.original?.content || "-";

        // Strip HTML for short preview
        const textContent = content.replace(/<[^>]*>/g, "");
        const shortText =
          textContent.length > 50
            ? `${textContent.substring(0, 50)}...`
            : textContent;

        // Create a Blob and object URL for the full HTML
        const blob = new Blob(
          [
            `<!DOCTYPE html><html><head><title>Blog Preview</title></head><body>${content}</body></html>`,
          ],
          { type: "text/html" }
        );
        const previewUrl = URL.createObjectURL(blob);

        return (
          <div>
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "6px 12px",
                background: "#007bff",
                color: "white",
                borderRadius: "4px",
                textDecoration: "none",
                fontSize: "0.85rem",
              }}
            >
              <Icon icon="mdi:eye" width="18" height="18" />
            </a>
          </div>
        );
      },
    }),

    // columnHelper.accessor("content", {
    //   header: "Content",
    //   Cell: (cell) => {
    //     const content = cell.row.original?.content || "-";
    //     // Strip HTML tags and limit length
    //     const textContent = content.replace(/<[^>]*>/g, "");
    //     return textContent.length > 50
    //       ? `${textContent.substring(0, 50)}...`
    //       : textContent;
    //   },
    // }),
    columnHelper.accessor("status", {
      header: "Status",
      Cell: (cell) => {
        const status = cell.row.original?.status;
        return (
          <Chip
            label={status === 1 ? "Published" : "Unpublished"}
            color={status === 1 ? "success" : "default"}
            size="small"
          />
        );
      },
    }),
    columnHelper.accessor("Actions", {
      header: "Actions",
      Cell: ({ cell }) => {
        const row = cell.row.original;
        const isPublished = row.status === 1;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={isPublished ? "Unpublish" : "Publish"}>
              <IconButton
                onClick={() => handleToggleStatus(row.id, row.status)}
                disabled={statusLoading[row.id]}
              >
                {statusLoading[row.id] ? (
                  <Icon
                    icon="mdi:loading"
                    className="animate-spin"
                    width="22"
                    height="22"
                  />
                ) : (
                  <Icon
                    icon={
                      isPublished ? "mdi:eye-outline" : "mdi:eye-off-outline"
                    }
                    color={isPublished ? "green" : "red"}
                    width="22"
                    height="22"
                  />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit Counselor" arrow>
              <button
                onClick={() => handleEditBlog(row.id, row)}
                disabled={editLoading[row.id]}
                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
              >
                <Icon icon="lucide:edit" />
              </button>
            </Tooltip>

            <Tooltip title="Delete Counselor" arrow>
              <button
                onClick={() => handleDeleteBlog(row.id)}
                disabled={deleteLoading[row.id]}
                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                style={{ cursor: "pointer" }}
              >
                <Icon icon="mingcute:delete-2-line" />
              </button>
            </Tooltip>
          </Box>
        );
      },
    }),
  ];

  return (
    <>
      <div className="add-blog-wrapper">
        <div className="top-buttons">
          <button
            className="btn-primary"
            onClick={() => setCategoryModal(true)}
            disabled={isLoading}
          >
            Create Category
          </button>
          <button
            className="btn-primary"
            onClick={() => setImageModal(true)}
            disabled={isLoading}
          >
            Generate Image Link
          </button>
        </div>

        <form className="blog-form" onSubmit={handleSubmitBlog}>
          <div className="form-row">
            <div className="form-group">
              <label>Post Category *</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                required
                disabled={isLoading}
              >
                <option value="">--Select category--</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Select Post Banner</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setBanner(e.target.files[0])}
                style={{ padding: "7px" }}
                disabled={isLoading}
              />
              {isEditMode && (
                <small style={{ color: "#666", fontSize: "12px" }}>
                  Leave empty to keep existing banner
                </small>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Post Title *</label>
            <input
              type="text"
              placeholder="Enter here"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Meta Tags</label>
              <input
                type="text"
                placeholder="Enter here"
                value={metaTags}
                onChange={(e) => setMetaTags(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                placeholder="Enter here"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Post Content *</label>
            <JoditEditor
              ref={editor}
              value={content}
              onChange={(newContent) => setContent(newContent)}
              // config={{
              //   readonly: isLoading,
              // }}
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={blogLoading}
            >
              {blogLoading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Blog"
                : "Create Blog"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleResetForm}
              disabled={isLoading}
            >
              {isEditMode ? "Cancel Edit" : "Reset Content"}
            </button>
          </div>
        </form>
      </div>

      {/* Category Modal */}
      {categoryModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h6 className="modal-title">Create Category</h6>
              <button
                className="modal-close"
                onClick={() => setCategoryModal(false)}
                disabled={isLoading}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <label className="form-label">Category Name</label>
                <div className="file-input-container">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter category name"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    disabled={isLoading}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddCategory();
                      }
                    }}
                  />
                  <button
                    className="btn-create"
                    onClick={handleAddCategory}
                    disabled={categoryLoading || !newCategory.trim()}
                  >
                    {categoryLoading ? "Creating..." : "Create"}
                  </button>
                </div>
              </div>

              <div className="categories-section">
                <div className="categories-list">
                  {categories.map((cat) => (
                    <div key={cat.id} className="category-item">
                      {editingCategory === cat.id ? (
                        <div className="edit-category-container">
                          <input
                            type="text"
                            value={editCategoryName}
                            onChange={(e) =>
                              setEditCategoryName(e.target.value)
                            }
                            className="edit-category-input"
                            disabled={isLoading}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleUpdateCategory(cat.id, editCategoryName);
                              }
                              if (e.key === "Escape") {
                                cancelEditing();
                              }
                            }}
                            autoFocus
                          />
                          <div className="edit-category-actions">
                            <button
                              className="save-btn"
                              onClick={() =>
                                handleUpdateCategory(cat.id, editCategoryName)
                              }
                              disabled={isLoading || !editCategoryName.trim()}
                            >
                              ✓
                            </button>
                            <button
                              className="cancel-btn"
                              onClick={cancelEditing}
                              disabled={isLoading}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className="category-name">{cat.name}</span>
                          <div className="category-actions">
                            <button
                              className="action-btn edit-btn"
                              onClick={() => startEditingCategory(cat)}
                              disabled={isLoading}
                              title="Edit category"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() =>
                                handleDeleteCategory(cat.id, cat.name)
                              }
                              disabled={isLoading}
                              title="Delete category"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {categories.length === 0 && !isLoading && (
                    <div className="no-categories">
                      No categories found. Create your first category above.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {imageModal && (
        <div className="modal-overlay">
          <div className="modal-container image-modal">
            <div className="modal-header">
              <h6 className="modal-title">Upload Image</h6>
              <button
                className="modal-close"
                onClick={() => setImageModal(false)}
                disabled={isLoading}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="upload-section">
                <label className="form-label">Upload Files</label>
                <div className="file-input-container">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleUploadImages}
                    className="file-input"
                    id="file-upload"
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`file-input-label ${
                      isLoading ? "disabled" : ""
                    }`}
                  >
                    Choose Files
                  </label>
                  <span className="file-input-text">
                    {uploadedImages.length > 0
                      ? `${uploadedImages.length} file(s) selected`
                      : "No file chosen"}
                  </span>
                  <button
                    className="btn-create"
                    onClick={handleCreateBanner}
                    disabled={imageLoading || uploadedImages.length === 0}
                  >
                    {imageLoading ? "Uploading..." : "Create"}
                  </button>
                </div>
              </div>

              <div className="images-grid">
                {/* Show local images (not yet uploaded) */}
                {uploadedImages.map((img, i) => (
                  <div className="image-item" key={`local-${i}`}>
                    <img
                      src={img.url || "/placeholder.svg"}
                      alt={`Upload ${i + 1}`}
                      className="image-preview"
                    />
                    <div className="image-overlay">
                      <div className="image-status">Local</div>
                      <button
                        className="overlay-btn"
                        onClick={() => handleCopyLink(img.url)}
                        title="Copy link"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        className="overlay-btn"
                        onClick={() => window.open(img.url, "_blank")}
                        title="View image"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="overlay-btn delete"
                        onClick={() => handleDeleteImage(i)}
                        title="Remove from queue"
                        disabled={isLoading}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Show uploaded banners from server */}
                {banners.map((banner, i) => (
                  <div className="image-item" key={`server-${banner.id}`}>
                    <img
                      src={`${baseUrl}${banner.name}`}
                      alt={`Banner ${i + 1}`}
                      className="image-preview"
                    />
                    <div className="image-overlay">
                      <div className="image-status uploaded">Uploaded</div>
                      <button
                        className="overlay-btn"
                        onClick={() =>
                          handleCopyLink(`${baseUrl}${banner.name}`)
                        }
                        title="Copy link"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        className="overlay-btn"
                        onClick={() =>
                          window.open(`${baseUrl}${banner.name}`, "_blank")
                        }
                        title="View image"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="overlay-btn delete"
                        onClick={() =>
                          handleDeleteBanner(banner.id, banner.name)
                        }
                        title="Delete image"
                        disabled={isLoading}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                {uploadedImages.length === 0 &&
                  banners.length === 0 &&
                  !isLoading && (
                    <div className="no-images">
                      No images found. Upload your first image above.
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10">
        <AdminCustomTable
          columns={columns}
          data={blogsDataList}
          loading={isLoading}
        />
      </div>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <style jsx>{`
        .add-blog-wrapper {
          padding: 20px;
          margin: auto;
          background: #fff;
        }

        .top-buttons {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
        }

        .btn-primary {
          background-color: #124178;
          color: white;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: opacity 0.2s;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background-color: #ccc;
          color: #333;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: opacity 0.2s;
        }

        .btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .blog-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .edit-mode-indicator {
          background: #e3f2fd;
          border: 1px solid #2196f3;
          border-radius: 6px;
          padding: 10px;
          color: #1976d2;
          font-weight: 500;
          text-align: center;
        }

        .form-row {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .form-group {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          font-weight: 600;
          margin-bottom: 8px;
        }

        .form-group input,
        .form-group select {
          padding: 10px;
          border: 1px solid #124178;
          border-radius: 6px;
          transition: opacity 0.2s;
        }

        .form-group input:disabled,
        .form-group select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-actions {
          display: flex;
          gap: 10px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3;
        }

        .modal-container {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 80vh;
          overflow: hidden;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .modal-container.image-modal {
          max-width: 700px;
        }

        .modal-header {
          background: #124178;
          padding: 5px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          color: white;
        }

        .modal-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 0.2s;
        }

        .modal-close:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .modal-close:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .modal-body {
          padding: 24px;
          max-height: calc(80vh - 80px);
          overflow-y: auto;
        }

        .form-section {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-input {
          width: 75%;
          padding: 12px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s, opacity 0.2s;
        }

        .form-input:focus {
          outline: none;
          border-color: #124178;
        }

        .form-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-create {
          background: #124178;
          color: white;
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s, opacity 0.2s;
        }

        .btn-create:hover:not(:disabled) {
          background: #0f3660;
        }

        .btn-create:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .categories-section {
          border-top: 1px solid #e5e7eb;
        }

        .categories-list {
          display: flex;
          flex-direction: column;
          max-height: 200px;
          overflow-y: auto;
          padding-right: 6px;
          margin-top: 12px;
        }

        .categories-list::-webkit-scrollbar {
          width: 6px;
        }
        .categories-list::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 4px;
        }
        .categories-list::-webkit-scrollbar-track {
          background-color: transparent;
        }

        .category-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 5px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .category-item:last-child {
          border-bottom: none;
        }

        .category-name {
          font-size: 14px;
          color: #374151;
        }

        .category-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s, opacity 0.2s;
        }

        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .edit-btn {
          color: #124178;
        }

        .edit-btn:hover:not(:disabled) {
          background: #f3f4f6;
        }

        .delete-btn {
          color: #ef4444;
        }

        .delete-btn:hover:not(:disabled) {
          background: #fef2f2;
        }

        .edit-category-container {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
        }

        .edit-category-input {
          flex: 1;
          padding: 6px 8px;
          border: 1px solid #124178;
          border-radius: 4px;
          font-size: 14px;
        }

        .edit-category-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .edit-category-actions {
          display: flex;
          gap: 4px;
        }

        .save-btn,
        .cancel-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: bold;
          transition: background-color 0.2s, opacity 0.2s;
        }

        .save-btn {
          color: #10b981;
        }

        .save-btn:hover:not(:disabled) {
          background: #f0fdf4;
        }

        .save-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .cancel-btn {
          color: #ef4444;
        }

        .cancel-btn:hover:not(:disabled) {
          background: #fef2f2;
        }

        .no-categories,
        .loading-categories {
          text-align: center;
          color: #6b7280;
          font-style: italic;
          padding: 20px;
        }

        /* Upload Modal Styles */
        .upload-section {
          margin-bottom: 24px;
        }

        .file-input-container {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .file-input {
          display: none;
        }

        .file-input-label {
          background: #124178;
          color: white;
          padding: 10px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s, opacity 0.2s;
        }

        .file-input-label:hover:not(.disabled) {
          background: #0f3660;
        }

        .file-input-label.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .file-input-text {
          color: #6b7280;
          font-size: 14px;
          flex: 1;
        }

        .images-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          max-height: 300px;
          overflow-y: auto;
          padding-right: 6px;
          border-radius: 6px;
          padding: 12px;
        }

        .images-grid::-webkit-scrollbar {
          width: 6px;
        }

        .images-grid::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 4px;
        }

        .images-grid::-webkit-scrollbar-track {
          background-color: transparent;
        }

        .image-item {
          position: relative;
          aspect-ratio: 3/2;
          border-radius: 8px;
          overflow: hidden;
          background: #f3f4f6;
        }

        .image-preview {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .image-overlay {
          position: absolute;
          top: 8px;
          right: 8px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .image-item:hover .image-overlay {
          opacity: 1;
        }

        .image-status {
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          text-align: center;
          font-weight: 500;
        }

        .image-status.uploaded {
          background: rgba(16, 185, 129, 0.8);
        }

        .overlay-btn {
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          padding: 6px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s, opacity 0.2s;
        }

        .overlay-btn:hover:not(:disabled) {
          background: rgba(0, 0, 0, 0.9);
        }

        .overlay-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .overlay-btn.delete {
          background: rgba(239, 68, 68, 0.8);
        }

        .overlay-btn.delete:hover:not(:disabled) {
          background: rgba(239, 68, 68, 1);
        }

        .no-images,
        .loading-images {
          grid-column: 1 / -1;
          text-align: center;
          color: #6b7280;
          font-style: italic;
          padding: 40px 20px;
        }

        @media (max-width: 768px) {
          .modal-container {
            width: 95%;
            margin: 20px;
          }

          .images-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .file-input-container {
            flex-direction: column;
            align-items: stretch;
          }

          .file-input-text {
            text-align: center;
            margin: 8px 0;
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </>
  );
};

export default AddBlogs;
